/**
 * Decides whether ONE dispatched leader request may be re-sent to a NEW leader
 * after the old one died mid-flight.
 *
 * The queue (`lib/leader-resource/leader-request-queue.ts`) deliberately knows no
 * SQL, so it takes this as an injected predicate. The rule it implements:
 *
 *   RE-SENDABLE  — a read-only statement issued OUTSIDE any explicit transaction.
 *                  Re-issuing it duplicates no observable effect, so at-most-once
 *                  buys nothing and rejecting only costs the caller a spurious
 *                  `LeaderChangedError`.
 *   NOT          — everything else. Settled decision D1 (at-most-once) exists so a
 *                  non-idempotent write can never double-apply; that is preserved
 *                  exactly.
 *
 * ── WHY TRANSACTION FRAMING MAKES A `SELECT` UNSAFE ───────────────────────────
 * drizzle implements `.transaction()` by issuing raw `begin` … `commit` as
 * SEPARATE calls on the connection, so a `SELECT` inside a transaction is
 * indistinguishable, statement-for-statement, from a standalone one. But its
 * meaning is not: it reads the dead leader's uncommitted transaction snapshot.
 * Re-sending it to a new leader would run it OUTSIDE that transaction (the
 * `begin` never reached the new leader, and its writes are gone), returning
 * data from a different snapshot while the caller believes it is still inside
 * its transaction. So transaction depth is tracked from the statement stream and
 * ANY statement at depth > 0 is classified NOT re-sendable.
 *
 * Fail-safe direction throughout: anything unrecognised — an unknown method, a
 * non-string SQL argument, a multi-statement script — is treated as MUTATING.
 * A wrongly-rejected read costs one spurious error; a wrongly-re-sent write
 * corrupts data.
 */
import { extractMutatedTable, splitSqlStatements } from "@-/com.npmjs.expo-sqlite";
import { classifyTransactionControl } from "#actor-sync-connection-lock";
import { TURSO_LEADER_METHODS } from "#open-leader-turso-resource";
import {
  getLeaderCallControl,
  isLeaderMaintenanceSql,
  parseLeaderTrackedCall,
} from "#leader-tracked-call";

/**
 * Is this one of the leader methods whose SQL is READ-ONLY BY CONTRACT? Checked
 * against the single `TURSO_LEADER_METHODS` allowlist rather than duplicated as
 * a hand-maintained list, so a method removed there stops matching here.
 *
 * `runAsync` / `execAsync` are the mutating entry points. `prepareValuesAsync` is
 * drizzle's `.values()` path: it is a read in practice, but it is also the path
 * used for `INSERT … RETURNING`, so it is deliberately NOT treated as read-only
 * here — the SQL check below would catch a plain `INSERT`, but the fail-safe
 * direction is cheaper than reasoning about every drizzle `.values()` caller.
 */
function isReadOnlyLeaderMethod(method: string): boolean {
  return TURSO_LEADER_METHODS.some(
    (candidate) =>
      candidate === method && (candidate === "getAllAsync" || candidate === "getFirstAsync"),
  );
}

/** Mutable transaction-depth tracker, fed by the dispatch stream in order. */
export interface LeaderTransactionState {
  depth: number;
}

export function createLeaderTransactionState(): LeaderTransactionState {
  return { depth: 0 };
}

/** First argument of every leader method is the SQL source. */
function getSqlArgument(args: readonly unknown[]): string | null {
  const [sql] = args;
  return typeof sql === "string" ? sql : null;
}

/**
 * Advance `state.depth` for a statement that is about to be dispatched.
 *
 * Mirrors `with-sqlite-busy-retry.ts`'s tracker, which solves the same problem on
 * the native connection. Counted optimistically at dispatch (we cannot await the
 * result here); a `begin` that ultimately fails leaves depth high for the rest of
 * the connection, which only makes classification MORE conservative.
 */
function applyTransactionControl(state: LeaderTransactionState, sql: string | null): void {
  const control = sql === null ? null : classifyTransactionControl(sql);
  if (control === "begin") {
    state.depth += 1;
    return;
  }
  if (control === "commit" || control === "rollback") {
    state.depth = Math.max(0, state.depth - 1);
  }
}

/**
 * Classify one request, then advance the transaction state.
 *
 * MUST be called exactly once per dispatched request, in dispatch order — the
 * depth tracking is a stream, not a pure function of the statement.
 */
export function classifyLeaderRequestResendability(
  state: LeaderTransactionState,
  entry: { args: readonly unknown[]; method: string },
): boolean {
  if (entry.method === "executeTracked") {
    const call = parseLeaderTrackedCall(entry.args[0]);
    const control = getLeaderCallControl(call.args);
    // A COMMIT is reconciled against its transaction's durable receipt. A body
    // statement must still fail with the old transaction, never run standalone.
    if (control === "commit") {
      return true;
    }
    if (control !== null || call.transactionId !== null) {
      return false;
    }
    // Connection maintenance is deliberately outside the receipt transaction.
    return !isLeaderMaintenanceSql(call.args);
  }
  if (entry.method === "updateSchema") {
    // Applies only the migrations the journal lacks, in one transaction of the leader's own. Sent
    // to a new leader it does the same work or none, so it is safe to send again.
    return true;
  }
  const sql = getSqlArgument(entry.args);
  const control = sql === null ? null : classifyTransactionControl(sql);

  // Inside an explicit transaction (or opening/closing one), nothing is
  // re-sendable — see the header. Checked BEFORE the depth is advanced so the
  // `begin` itself is also classified unsafe.
  const insideTransaction = state.depth > 0 || control !== null;

  let resendable = false;
  if (!insideTransaction && sql !== null && isReadOnlyLeaderMethod(entry.method)) {
    // Belt and braces on top of the method allowlist: a read method whose SQL
    // nonetheless mutates (e.g. `INSERT … RETURNING` sent through `getAllAsync`)
    // must not be re-sent. `splitSqlStatements` so a multi-statement string
    // cannot smuggle a write past a leading `SELECT`.
    resendable = splitSqlStatements(sql).every(
      (statement) => extractMutatedTable(statement) === null,
    );
  }

  applyTransactionControl(state, sql);
  return resendable;
}
